import { create } from 'zustand';
import type { Camera } from '@/types/raspi';
import { getAllCameras, updateCamera, deleteCamera } from '@/services/api';

interface CameraStore {
  // Camera Data (Database-Aligned)
  allCameras: Record<string, Camera>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAllCameras: () => Promise<void>;
  updateCameraFromWebSocket: (camera_id: string, data: Partial<Camera>) => void;
  updateCameraFromUser: (camera_id: string, data: Partial<Camera>) => Promise<boolean>;
  removeCamera: (camera_id: string) => Promise<boolean>;
  deleteCamera: (camera_id: string) => Promise<boolean>;
  handleCameraFrame: (frameData: {
    camera_id: string;
    frame_data: string;
    timestamp: string;
    frame_size: number;
  }) => void;
  getCamera: (camera_id: string) => Camera | undefined;
  clearError: () => void;
  resetStore: () => void;
}

export const useCameraStore = create<CameraStore>((set, get) => ({
  // Initialize with default values
  allCameras: {},
  isLoading: false,
  error: null,

  fetchAllCameras: async () => {
    set({ isLoading: true, error: null });
    try {
      const camerasData = await getAllCameras();
      
      // Convert string dates to Date objects for all cameras
      const convertedCameras: Record<string, Camera> = {};
      Object.entries(camerasData).forEach(([id, camera]) => {
        convertedCameras[id] = {
          ...camera,
          last_update: camera.last_update instanceof Date ? camera.last_update : new Date(camera.last_update),
        };
      });
      
      // ✅ Fix 2: Merge instead of replace - preserve WebSocket updates
      set((state) => ({
        allCameras: {
          ...state.allCameras,  // Keep existing store data (may have WebSocket updates)
          ...convertedCameras,  // Merge with API data (adds new entities, updates existing)
        },
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch cameras' 
      });
    }
  },

  updateCameraFromWebSocket: (camera_id: string, data: Partial<Camera>) => {
    set((state) => {
      const updatedCameras = {
        ...state.allCameras,
        [camera_id]: {
          ...state.allCameras[camera_id],
          ...data,
        },
      };
      
      return { allCameras: updatedCameras };
    });
  },

  updateCameraFromUser: async (camera_id: string, data: Partial<Camera>) => {
    
    // VALIDATION: Ensure camera exists in store before any operation
    const originalData = get().allCameras[camera_id];
    if (!originalData) {
      console.error('🔄 [CAMERA STORE] ERROR: Cannot update non-existent camera:', camera_id);
      return false;
    }
    
    // VALIDATION: Ensure required fields are present
    if (data.camera_gauge_id !== undefined && typeof data.camera_gauge_id !== 'string') {
      console.error('🔄 [CAMERA STORE] ERROR: Invalid camera_gauge_id type:', data.camera_gauge_id);
      return false;
    }
    if (data.camera_led_id !== undefined && typeof data.camera_led_id !== 'string') {
      console.error('🔄 [CAMERA STORE] ERROR: Invalid camera_led_id type:', data.camera_led_id);
      return false;
    }
    
    try {
      // Update store immediately (store-first approach)
      set((state) => {
        const updatedCameras = {
          ...state.allCameras,
          [camera_id]: {
            ...state.allCameras[camera_id],
            ...data,
          },
        };
        
        return { allCameras: updatedCameras };
      });

      // Sync to database using new specific API method
      const success = await updateCamera(camera_id, data);
      
      if (!success) {
        // Revert store update on failure using captured original data
        set((state) => ({
          allCameras: {
            ...state.allCameras,
            [camera_id]: originalData,
          },
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.error('🔄 [CAMERA STORE] Error updating camera:', error);
      // Also revert on exception using captured original data
      if (originalData) {
        set((state) => ({
          allCameras: {
            ...state.allCameras,
            [camera_id]: originalData,
          },
        }));
      }
      return false;
    }
  },

  removeCamera: async (camera_id: string) => {
    
    // Capture original data BEFORE optimistic update
    const originalData = get().allCameras[camera_id];
    
    try {
      // Remove from store immediately (store-first approach)
      set((state) => {
        const { [camera_id]: removed, ...remainingCameras } = state.allCameras;
        return { allCameras: remainingCameras };
      });

      // Sync deletion to database using new specific API method
      const success = await deleteCamera(camera_id);
      
      if (!success) {
        // Revert store update on failure using captured original data
        set((state) => ({
          allCameras: {
            ...state.allCameras,
            [camera_id]: originalData,
          },
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.error('🔄 [CAMERA STORE] Error removing camera:', error);
      // Also revert on exception using captured original data
      if (originalData) {
        set((state) => ({
          allCameras: {
            ...state.allCameras,
            [camera_id]: originalData,
          },
        }));
      }
      return false;
    }
  },

  deleteCamera: async (camera_id: string) => {
    try {
      // Use the new specific API method
      const success = await deleteCamera(camera_id);
      
      if (success) {
        // Remove from store on successful deletion
        set((state) => {
          const { [camera_id]: removed, ...remainingCameras } = state.allCameras;
          return { allCameras: remainingCameras };
        });
      }
      
      return success;
    } catch (error) {
      console.error('🔄 [CAMERA STORE] Error deleting camera:', error);
      return false;
    }
  },

  handleCameraFrame: (frameData: {
    camera_id: string;
    frame_data: string;
    timestamp: string;
    frame_size: number;
  }) => {
    // Store frame data for live streaming
    set((state) => ({
      allCameras: {
        ...state.allCameras,
        [frameData.camera_id]: {
          ...state.allCameras[frameData.camera_id],
          currentFrame: frameData.frame_data,
          frameTimestamp: frameData.timestamp,
          frameSize: frameData.frame_size,
        },
      },
    }));
  },

  getCamera: (camera_id: string) => {
    return get().allCameras[camera_id];
  },

  clearError: () => {
    set({ error: null });
  },

  resetStore: () => {
    set({
      allCameras: {},
      isLoading: false,
      error: null,
    });
  },
}));

export default useCameraStore; 